import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SplitPayTransactionList } from '../components/SplitPayTransactionList';
import { SplitPayTransactionFiltersComponent, SplitPayTransactionFilters } from '../components/SplitPayTransactionFilters';
import { splitPayTransactionApi } from '../lib/api/splitPayTransactionApi';

const SplitPayTransactionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<SplitPayTransactionFilters>({});
  const [appliedFilters, setAppliedFilters] = useState<SplitPayTransactionFilters>({});
  const [isDownloading, setIsDownloading] = useState(false);

  const handleFiltersChange = (newFilters: SplitPayTransactionFilters) => {
    setFilters(newFilters);
  };

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    toast.success('Filters applied successfully');
  };

  const handleClearFilters = () => {
    setFilters({});
    setAppliedFilters({});
    toast.success('Filters cleared');
  };

  const handleDownloadCSV = async () => {
    try {
      setIsDownloading(true);
      const csvData = await splitPayTransactionApi.downloadSplitPayTransactionsCSV(
        undefined, // limit
        undefined, // offset
        appliedFilters.payerId,
        appliedFilters.paymentMethodId,
        appliedFilters.status === '' ? undefined : appliedFilters.status,
        appliedFilters.startDate,
        appliedFilters.endDate,
        appliedFilters.minAmount,
        appliedFilters.maxAmount
      );

      // Create and download the CSV file
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `split-pay-transactions-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('CSV file downloaded successfully');
    } catch (error) {
      console.error('Error downloading CSV:', error);
      toast.error('Failed to download CSV file');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCreateTransaction = () => {
    navigate('/splitpay-transactions/create');
  };

  const handleViewTransactionDetails = (transactionId: string) => {
    navigate(`/splitpay-transactions/${transactionId}`);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <Link to="/">
          <Button variant="outline" className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </Link>
      </div>

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Transactions</h1>
        <p className="text-gray-600">
          Manage and monitor transactions across the platform.
        </p>
      </div>

      <SplitPayTransactionFiltersComponent
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        onDownloadCSV={handleDownloadCSV}
        isLoading={isDownloading}
      />

      <SplitPayTransactionList
        onCreateTransaction={handleCreateTransaction}
        onViewTransactionDetails={handleViewTransactionDetails}
        filters={appliedFilters}
      />
    </div>
  );
};

export default SplitPayTransactionsPage;
